import QRCode from "qrcode";
import { useEffect, useId, useRef, useState } from "react";
import { ChoiceField } from "@/components/tool/CalcFields";
import { m } from "@/paraglide/messages";

type Ecc = "L" | "M" | "Q" | "H";

export function QrGenerator() {
  const id = useId();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [text, setText] = useState("https://toolverse.me");
  const [ecc, setEcc] = useState<Ecc>("M");
  const [error, setError] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const value = text.trim();
    if (!value) {
      canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
      setError(false);
      return;
    }
    QRCode.toCanvas(
      canvas,
      value,
      { errorCorrectionLevel: ecc, width: 256, margin: 2 },
      (err) => setError(err != null),
    );
  }, [text, ecc]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "qr-code.png";
    a.click();
  };

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <div className="space-y-1.5">
        <label htmlFor={id} className="block text-sm text-muted-foreground">
          {m.qr_content()}
        </label>
        <textarea
          id={id}
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={m.qr_placeholder()}
          className="w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm text-neutral-900"
        />
      </div>

      <ChoiceField
        label={m.qr_ecc()}
        value={ecc}
        onChange={setEcc}
        options={[
          { value: "L", label: m.qr_ecc_l() },
          { value: "M", label: m.qr_ecc_m() },
          { value: "Q", label: m.qr_ecc_q() },
          { value: "H", label: m.qr_ecc_h() },
        ]}
      />

      <div className="flex flex-col items-center gap-4">
        <div className="rounded-xl bg-white p-4">
          <canvas
            ref={canvasRef}
            data-testid="qr-canvas"
            aria-label={m.qr_alt()}
            className="h-64 w-64"
          />
        </div>
        {error ? (
          <p className="text-sm text-rose-600 dark:text-rose-400">
            {m.qr_too_long()}
          </p>
        ) : (
          <button
            type="button"
            onClick={download}
            className="sci-result-zone sci-result rounded-md px-5 py-2.5 text-sm font-semibold"
          >
            {m.qr_download()}
          </button>
        )}
      </div>
    </div>
  );
}
